import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { ChapterDto, ParagraphDto } from '../../api/queries/books';
import { useBookGaps } from '../../api/queries/consistency';
import { BulkGenerationDialog } from './BulkGenerationDialog';
import { ChapterNarrationDialog } from './ChapterNarrationDialog';

interface BookHealthDashboardProps {
  bookId: string;
  chapters: ChapterDto[];
  paragraphs: ParagraphDto[];
  onNavigateToParagraph?: (paragraphId: string) => void;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

function countWords(text: string): number {
  const stripped = stripHtml(text);
  if (!stripped) return 0;
  return stripped.split(/\s+/).filter(Boolean).length;
}

function coverageColor(percent: number): 'success' | 'warning' | 'error' {
  if (percent >= 80) return 'success';
  if (percent >= 40) return 'warning';
  return 'error';
}

function coverageLabel(percent: number): string {
  if (percent >= 80) return 'Good';
  if (percent >= 40) return 'Partial';
  return 'Low';
}

export function BookHealthDashboard({
  bookId,
  chapters,
  paragraphs,
  onNavigateToParagraph,
}: BookHealthDashboardProps) {
  const { data: gaps, isLoading } = useBookGaps(bookId);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [narrationOpen, setNarrationOpen] = useState(false);
  const [showMissingImages, setShowMissingImages] = useState(false);
  const [showMissingAudio, setShowMissingAudio] = useState(false);

  const narrationChapterId = chapters.length > 0 ? chapters[0]!.id : '';

  const totalWords = useMemo(
    () => paragraphs.reduce((sum, p) => sum + countWords(p.contentHtml), 0),
    [paragraphs],
  );

  const totalParagraphs = gaps?.totalParagraphs ?? paragraphs.length;
  const paragraphsWithImages =
    totalParagraphs - (gaps?.paragraphsMissingImages ?? totalParagraphs);
  const paragraphsWithAudio =
    totalParagraphs - (gaps?.paragraphsMissingAudio ?? totalParagraphs);

  const imageCoverage =
    totalParagraphs > 0 ? (paragraphsWithImages / totalParagraphs) * 100 : 0;
  const audioCoverage =
    totalParagraphs > 0 ? (paragraphsWithAudio / totalParagraphs) * 100 : 0;

  // Build a paragraph lookup for click-to-navigate.
  const paragraphById = useMemo(
    () => new Map(paragraphs.map((p) => [p.id, p])),
    [paragraphs],
  );

  const handleBulkAudio = () => {
    setNarrationOpen(true);
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Book Health
        </Typography>
        <Stack spacing={1.5}>
          <Skeleton variant="rectangular" height={24} />
          <Skeleton variant="rectangular" height={24} />
          <Skeleton variant="rectangular" height={60} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Book Health
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Summary stats */}
      <Stack spacing={1} sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <StatChip label="Chapters" value={chapters.length} />
          <StatChip label="Paragraphs" value={totalParagraphs} />
          <StatChip label="Words" value={totalWords.toLocaleString()} />
        </Stack>
      </Stack>

      {/* Coverage bars */}
      <Stack spacing={2} sx={{ mb: 2.5 }}>
        <CoverageBar
          icon={<ImageIcon fontSize="small" />}
          label="Image coverage"
          current={paragraphsWithImages}
          total={totalParagraphs}
          percent={imageCoverage}
        />
        <CoverageBar
          icon={<AudiotrackIcon fontSize="small" />}
          label="Audio coverage"
          current={paragraphsWithAudio}
          total={totalParagraphs}
          percent={audioCoverage}
        />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Missing assets list — now clickable */}
      <Stack spacing={1} sx={{ mb: 2.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Missing Assets
        </Typography>

        {gaps?.paragraphsMissingImages ? (
          <>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{ py: 0, cursor: 'pointer' }}
              onClick={() => setShowMissingImages((v) => !v)}
              action={
                showMissingImages ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )
              }
            >
              <Typography variant="caption">
                {gaps.paragraphsMissingImages} paragraph
                {gaps.paragraphsMissingImages !== 1 ? 's' : ''} missing images
              </Typography>
            </Alert>
            <Collapse in={showMissingImages}>
              <MissingParagraphList
                paragraphIds={gaps.missingImageParagraphIds ?? []}
                paragraphById={paragraphById}
                onNavigate={onNavigateToParagraph}
              />
            </Collapse>
          </>
        ) : (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="small" />}
            sx={{ py: 0 }}
          >
            <Typography variant="caption">
              All paragraphs have images
            </Typography>
          </Alert>
        )}

        {gaps?.paragraphsMissingAudio ? (
          <>
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{ py: 0, cursor: 'pointer' }}
              onClick={() => setShowMissingAudio((v) => !v)}
              action={
                showMissingAudio ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )
              }
            >
              <Typography variant="caption">
                {gaps.paragraphsMissingAudio} paragraph
                {gaps.paragraphsMissingAudio !== 1 ? 's' : ''} missing audio
              </Typography>
            </Alert>
            <Collapse in={showMissingAudio}>
              <MissingParagraphList
                paragraphIds={gaps.missingAudioParagraphIds ?? []}
                paragraphById={paragraphById}
                onNavigate={onNavigateToParagraph}
              />
            </Collapse>
          </>
        ) : (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="small" />}
            sx={{ py: 0 }}
          >
            <Typography variant="caption">All paragraphs have audio</Typography>
          </Alert>
        )}

        {gaps?.paragraphsMissingMetadata ? (
          <Alert severity="info" sx={{ py: 0 }}>
            <Typography variant="caption">
              {gaps.paragraphsMissingMetadata} paragraph
              {gaps.paragraphsMissingMetadata !== 1 ? 's' : ''} missing metadata
            </Typography>
          </Alert>
        ) : null}

        {gaps?.entitiesMissingReferenceImage ? (
          <Alert severity="info" sx={{ py: 0 }}>
            <Typography variant="caption">
              {gaps.entitiesMissingReferenceImage} entit
              {gaps.entitiesMissingReferenceImage !== 1 ? 'ies' : 'y'} missing
              reference image
            </Typography>
          </Alert>
        ) : null}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Bulk actions */}
      <Stack spacing={1}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Bulk Actions
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ImageIcon fontSize="small" />}
            onClick={() => setBulkDialogOpen(true)}
            disabled={!gaps?.paragraphsMissingImages}
          >
            Generate all missing images
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AudiotrackIcon fontSize="small" />}
            onClick={handleBulkAudio}
            disabled={!gaps?.paragraphsMissingAudio}
          >
            Generate all audio
          </Button>
        </Stack>
      </Stack>

      <BulkGenerationDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        bookId={bookId}
        paragraphs={paragraphs}
        mode="book"
      />
      {narrationChapterId && (
        <ChapterNarrationDialog
          open={narrationOpen}
          onClose={() => setNarrationOpen(false)}
          bookId={bookId}
          chapterId={narrationChapterId}
          paragraphs={paragraphs}
        />
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

function StatChip({ label, value }: { label: string; value: number | string }) {
  return (
    <Chip
      size="small"
      label={
        <span>
          <strong>{value}</strong> {label.toLowerCase()}
        </span>
      }
      variant="outlined"
    />
  );
}

interface CoverageBarProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  total: number;
  percent: number;
}

function CoverageBar({
  icon,
  label,
  current,
  total,
  percent,
}: CoverageBarProps) {
  const color = coverageColor(percent);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        {icon}
        <Typography variant="caption" sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={`${Math.round(percent)}% ${coverageLabel(percent)}`}
          color={color}
          variant="outlined"
          sx={{ fontSize: 11 }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={color}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 50, textAlign: 'right' }}
        >
          {current}/{total}
        </Typography>
      </Stack>
    </Box>
  );
}

interface MissingParagraphListProps {
  paragraphIds: string[];
  paragraphById: Map<string, ParagraphDto>;
  onNavigate?: (id: string) => void;
}

function MissingParagraphList({
  paragraphIds,
  paragraphById,
  onNavigate,
}: MissingParagraphListProps) {
  if (paragraphIds.length === 0) return null;

  return (
    <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
      {paragraphIds.slice(0, 20).map((id) => {
        const p = paragraphById.get(id);
        const preview = p
          ? stripHtml(p.contentHtml).slice(0, 60) || 'Empty paragraph'
          : `Paragraph ${id.slice(0, 8)}...`;
        return (
          <ListItemButton
            key={id}
            onClick={() => onNavigate?.(id)}
            sx={{ py: 0.25 }}
          >
            <ListItemText
              primary={preview}
              primaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </ListItemButton>
        );
      })}
      {paragraphIds.length > 20 && (
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.5 }}
          color="text.secondary"
        >
          +{paragraphIds.length - 20} more
        </Typography>
      )}
    </List>
  );
}
